import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Sparkles } from 'lucide-react';
import closeIntelApi from './closeIntelApi';
import FindingWorkbench from './FindingWorkbench';
import SectionGuide from './SectionGuide';
import { fmtNumber, money, scoreFactors, selectNextOpenFinding } from './workbenchHelpers';

const previousMonth = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const asApiPeriod = (month) => (String(month || '').length === 7 ? `${month}-01` : month || undefined);

const extractJudgedFinding = (payload, fallback) => {
  if (!payload) return fallback;
  if (payload.finding) return payload.finding;
  if (Array.isArray(payload.findings)) return payload.findings.find((row) => row.id === fallback.id) || fallback;
  if (Array.isArray(payload.rows)) return payload.rows.find((row) => row.id === fallback.id) || fallback;
  if (payload.id) return payload;
  return fallback;
};

const mergeFindingRows = (rows, updates) => {
  const byId = new Map((updates || []).map((row) => [row.id, row]));
  return rows.map((row) => (byId.has(row.id) ? { ...row, ...byId.get(row.id) } : row));
};

function useElapsed(active) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return undefined;
    }
    const started = Date.now();
    setSeconds(0);
    const id = window.setInterval(() => {
      setSeconds(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return seconds;
}

function SpinnerLabel({ active, children }) {
  return (
    <>
      {active && <span className="ci-spinner" aria-hidden="true" />}
      <span className={active ? 'ci-running-text' : ''}>{children}</span>
    </>
  );
}

function RunCountsStrip({ counts, flash }) {
  const c = counts || {};
  const chain = [
    ['candidates', c.nCandidates ?? c.candidates],
    ['survived', c.nSurvived ?? c.survived],
    ['suppressed', c.nSuppressed ?? c.suppressed],
    ['findings', c.nFindings ?? c.findings]
  ];
  return (
    <div className={`ci-count-strip ${flash ? 'flash' : ''}`}>
      {chain.map(([label, value], index) => (
        <React.Fragment key={label}>
          <span className="ci-count-cell">
            <span>{label}</span>
            <strong>{fmtNumber(value, 0)}</strong>
          </span>
          {index < chain.length - 1 && <span className="ci-chain-arrow">→</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

const categoryLabel = (category) => {
  const labels = {
    loans: 'LOANS',
    deposits: 'DEPOSITS',
    'interest income': 'NII',
    'interest expense': 'NIE-INT',
    'non-interest revenue': 'NIR',
    'non-interest expense': 'NIE',
    other: 'OTHER'
  };
  return labels[category] || String(category || 'OTHER').toUpperCase();
};

function QueueItem({ finding, active, onSelect }) {
  const factors = scoreFactors(finding);
  return (
    <button
      type="button"
      className={`ci-queue-item ${active ? 'active' : ''}`}
      onClick={() => onSelect(finding.id)}
    >
      <span className="ci-queue-title">{finding.planningEntity || finding.entity || 'Company'}</span>
      <span className="ci-queue-subtitle">{finding.glGroup || finding.glAccount || 'GL group n/a'}</span>
      <span className="ci-queue-meta">
        <span className="ci-category-chip">{categoryLabel(finding.category)}</span>
        <span className="ace-pill">{finding.measure || 'MTD'}</span>
        <span className="ci-mono">z {fmtNumber(factors.z || finding.surpriseZ, 2)}</span>
        <span className="ci-mono">score {fmtNumber(finding.score, 1)}</span>
      </span>
      <span className="ci-queue-chips">
        <span className="ace-pill">{finding.criticality || 'unjudged'}</span>
        <span className="ace-pill">{finding.status || 'open'}</span>
      </span>
      <span className="ci-queue-amount">{money(finding.actualValue ?? finding.value ?? finding.amount)}</span>
    </button>
  );
}

const readMonitorParams = () => {
  if (typeof window === 'undefined') return { period: '', finding: '' };
  const params = new URLSearchParams(window.location.search);
  const period = params.get('period') || '';
  return {
    period: /^\d{4}-\d{2}$/.test(period) ? period : '',
    finding: params.get('finding') || ''
  };
};

export default function MonitorPage() {
  const deepLink = useMemo(readMonitorParams, []);
  const [asOfPeriod, setAsOfPeriod] = useState(() => deepLink.period || previousMonth());
  const [dryRun, setDryRun] = useState(true);
  const [findings, setFindings] = useState([]);
  const [runResult, setRunResult] = useState(null);
  const [selectedId, setSelectedId] = useState(() => deepLink.finding);
  const [contextById, setContextById] = useState({});
  const [contextState, setContextState] = useState({});
  const [busy, setBusy] = useState(false);
  const [judgingId, setJudgingId] = useState('');
  const [batchBusy, setBatchBusy] = useState(false);
  const [error, setError] = useState('');
  const [flashCounts, setFlashCounts] = useState(false);
  const [queueOrder, setQueueOrder] = useState('balanced');

  const runElapsed = useElapsed(busy);
  const batchElapsed = useElapsed(batchBusy);

  const queueFindings = useMemo(() => {
    if (queueOrder === 'score') {
      return [...findings].sort((a, b) => Number(b.score || 0) - Number(a.score || 0));
    }
    return findings;
  }, [findings, queueOrder]);

  const selectedFinding = queueFindings.find((finding) => finding.id === selectedId) || queueFindings[0] || null;

  const loadFindings = async () => {
    setError('');
    try {
      const payload = await closeIntelApi.listFindings({
        asOfPeriod: asApiPeriod(asOfPeriod),
        order: queueOrder
      });
      const rows = payload?.rows || [];
      setFindings(rows);
      setSelectedId((current) => current || rows[0]?.id || '');
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const firstLoadRef = useRef(true);
  useEffect(() => {
    // Preserve a deep-linked ?finding selection on the initial load only.
    if (!firstLoadRef.current) setSelectedId('');
    firstLoadRef.current = false;
    setContextById({});
    setContextState({});
    loadFindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOfPeriod, queueOrder]);

  useEffect(() => {
    // Never clear while findings are still loading — that would clobber a
    // deep-linked ?finding selection before the queue arrives.
    if (!queueFindings.length) return;
    if (!selectedId || !queueFindings.some((finding) => finding.id === selectedId)) {
      setSelectedId(queueFindings[0].id);
    }
  }, [selectedId, queueFindings]);

  const loadContext = async (findingId, force = false) => {
    if (!findingId || (!force && contextById[findingId])) return;
    setContextState((state) => ({ ...state, [findingId]: { status: 'loading' } }));
    try {
      const payload = await closeIntelApi.findingContext(findingId);
      setContextById((state) => ({ ...state, [findingId]: payload }));
      setContextState((state) => ({ ...state, [findingId]: { status: 'ready' } }));
    } catch (e) {
      const retry = e.status === 404 || String(e.message || '').includes('404');
      setContextState((state) => ({
        ...state,
        [findingId]: { status: retry ? 'retry' : 'error', error: String(e.message || e) }
      }));
    }
  };

  useEffect(() => {
    if (selectedFinding?.id) loadContext(selectedFinding.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFinding?.id]);

  const flashCounterStrip = () => {
    setFlashCounts(true);
    window.setTimeout(() => setFlashCounts(false), 900);
  };

  const runMonitor = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await closeIntelApi.runMonitor({ asOfPeriod: asApiPeriod(asOfPeriod), dryRun });
      setRunResult(result);
      await loadFindings();
      flashCounterStrip();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const judgeFinding = async (finding) => {
    setJudgingId(finding.id);
    setError('');
    try {
      const result = await closeIntelApi.judgeFinding(finding.id);
      const updated = extractJudgedFinding(result, finding);
      setFindings((rows) => rows.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setJudgingId('');
    }
  };

  const judgeBatch = async () => {
    setBatchBusy(true);
    setError('');
    try {
      const result = await closeIntelApi.judgeBatch({ asOfPeriod: asApiPeriod(asOfPeriod), dryRun });
      const updates = result?.findings || result?.rows || [];
      if (updates.length) setFindings((rows) => mergeFindingRows(rows, updates));
      else await loadFindings();
      flashCounterStrip();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBatchBusy(false);
    }
  };

  const saveDisposition = async (finding, payload) => {
    const result = await closeIntelApi.dispositionFinding(finding.id, payload);
    const updated = result?.finding || { ...finding, status: 'dispositioned', disposition: result?.disposition || payload.verdict };
    const nextRows = findings.map((row) => (row.id === updated.id ? { ...row, ...updated } : row));
    setFindings(nextRows);
    setSelectedId(selectNextOpenFinding(nextRows, updated.id));
  };

  const counts = runResult?.counts || {
    findings: queueFindings.length,
    candidates: runResult?.nCandidates,
    survived: runResult?.nSurvived,
    suppressed: runResult?.nSuppressed
  };

  const anyRunning = busy || batchBusy;

  return (
    <section className="ace-panel ci-monitor-shell">
      <div className="ci-monitor-header">
        <div>
          <div className="ace-eyebrow">CI_01 <span aria-hidden>•</span> Monitor</div>
          <h2>Analyst decision workbench</h2>
          {anyRunning && (
            <div className="ci-running-counter">
              RUNNING · {busy ? runElapsed : batchElapsed}s
            </div>
          )}
        </div>
        <div className="ci-monitor-controls">
          <input
            type="month"
            value={asOfPeriod}
            onChange={(event) => setAsOfPeriod(event.target.value)}
            className="ace-input ci-tip"
            data-tip="Close month to scan for anomalies"
          />
          <label className="ace-pill ci-tip" data-tip="Nothing is persisted; safe to test">
            <input type="checkbox" checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />
            Dry run
          </label>
          <button
            type="button"
            className="ace-pill ci-pinpoint-button ci-tip"
            data-tip="Scan every GL cell for this month and score surprises (~1 min)"
            disabled={busy || batchBusy}
            onClick={runMonitor}
          >
            {!busy && <Play size={13} />}
            <SpinnerLabel active={busy}>{busy ? 'running' : 'Run monitor'}</SpinnerLabel>
          </button>
          <button
            type="button"
            className="ace-pill ci-pinpoint-button ci-tip"
            data-tip="Draft AI judgment briefs for the top open findings"
            disabled={busy || batchBusy}
            onClick={judgeBatch}
          >
            {!batchBusy && <Sparkles size={13} />}
            <SpinnerLabel active={batchBusy}>{batchBusy ? 'running' : 'Judge batch'}</SpinnerLabel>
          </button>
        </div>
      </div>

      {anyRunning && <div className="ci-progress-sweep" aria-hidden="true" />}
      <SectionGuide guideId="monitor" />
      <RunCountsStrip counts={counts} flash={flashCounts} />
      <div className="ci-queue-order-toggle" role="group" aria-label="Finding queue order">
        <button
          type="button"
          className={`ace-pill ${queueOrder === 'balanced' ? 'ace-pill--active' : ''}`}
          data-tip="Round-robin across loans, deposits, NII, NIR, NIE so triage isn't dominated by balance-sheet dollars"
          onClick={() => setQueueOrder('balanced')}
        >
          BALANCED MIX
        </button>
        <button
          type="button"
          className={`ace-pill ${queueOrder === 'score' ? 'ace-pill--active' : ''}`}
          data-tip="Pure score order"
          onClick={() => setQueueOrder('score')}
        >
          RAW SCORE
        </button>
      </div>
      {error && <p className="ci-error">{error}</p>}

      <div className="ci-workbench-layout">
        <aside className="ci-queue-rail" aria-label="Finding queue">
          {queueFindings.map((finding) => (
            <QueueItem
              key={finding.id}
              finding={finding}
              active={selectedFinding?.id === finding.id}
              onSelect={setSelectedId}
            />
          ))}
          {!queueFindings.length && <div className="ci-empty-state">No findings returned for this period.</div>}
        </aside>
        <FindingWorkbench
          finding={selectedFinding}
          context={contextById[selectedFinding?.id]}
          contextState={contextState[selectedFinding?.id]}
          onRetryContext={() => loadContext(selectedFinding?.id, true)}
          onJudge={judgeFinding}
          judging={judgingId === selectedFinding?.id}
          onDispositionSaved={saveDisposition}
        />
      </div>
    </section>
  );
}
